import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useObserveRef, isFunction, useForceRender } from '@chkp/mwc-common';

// todo: extract sizing logic to a SizeObserver / useSizeObserver

/**
 * Wrapper for sizing virtual content, which total size is known without rendering it.
 *
 * Works by rendering a fake div of the total size within a wrapper div, and measuring resulting wrapper size.
 * Use standard css on this Wrapper and on the surrounding content to affect sizing behavior, e.g. minWidth, flex, display etc.
 */
const VirtualizedSizer = ({
	children,
	style,
	className,
	totalHeightObservable,
	totalWidthObservable,
	onSize, // callback for measured size
	renderWithoutSize, // render content before size is calculated (default false)
}) => {
	const [, forceRender] = useForceRender();
	const isRenderProp = isFunction(children);

	const ref = useRef(); // measured element
	const fakeRef = useRef(); // inner fake element with totalSize
	const sizeRef = useRef({}); // measured size

	// Track latest callback (to avoid redundant updates if callback changes)
	const onSizeRef = useRef();
	onSizeRef.current = onSize;

	// Update fake element according to total size
	useObserveRef(totalHeightObservable, (height) => {
		fakeRef.current.style.height = height ? height + 'px' : 0;
	});
	useObserveRef(totalWidthObservable, (width) => {
		fakeRef.current.style.width = width ? width + 'px' : 0;
	});

	const updateSize = useCallback(() => {
		const { offsetWidth: width, offsetHeight: height } = ref.current;
		onSizeRef.current({ width, height });

		sizeRef.current = { width, height };
		if (isRenderProp) {
			forceRender(); // re-render in order to provide new sizing to children render-prop
		}
	}, [forceRender, isRenderProp]);

	useEffect(() => {
		const el = ref.current;
		updateSize();
		const resizeObserver = new ResizeObserver(updateSize);
		resizeObserver.observe(el);
		return () => resizeObserver.unobserve(el);
	}, [updateSize]);

	const shouldRender =
		renderWithoutSize || (sizeRef.current?.width && sizeRef.current?.height);

	const content = shouldRender
		? isRenderProp
			? children(sizeRef.current)
			: children
		: null;

	return (
		<div
			ref={ref}
			style={{
				...style,
				overflow: 'hidden',
			}}
			className={className}
			data-tag={'virtualized-sizer'}
		>
			<div
				ref={fakeRef}
				style={{
					height: totalHeightObservable.current || 0,
					width: totalWidthObservable.current || 0,
					overflow: 'visible',
				}}
			>
				{content}
			</div>
		</div>
	);
};

export { VirtualizedSizer };
